import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import { apiErrorMessage, SESSION_EXPIRED_MESSAGE } from '../utils/apiErrorMessage';

export interface AiDocumentField {
  key: string;
  label: string;
  sampleValue?: string;
  required: boolean;
}

export interface AiDocumentAsset {
  id: string;
  type: string;
  url: string;
  width?: number;
  height?: number;
}

export interface AiDocumentTemplateOption {
  id: number;
  name: string;
  description?: string | null;
  layoutId: string;
  status: string;
}

export interface SectorAiDocumentConfig {
  sectorId: number;
  status: 'empty' | 'processing' | 'ready' | 'error';
  processingStep?: string | null;
  processingError?: string | null;
  enabled: boolean;
  documentPrompt: string;
  linkedAgentId?: number | null;
  linkedAgents?: {
    receptive?: number | null;
    campaign?: number | null;
    followup?: number | null;
    crm?: number | null;
  };
  templateId?: number | null;
  templateName?: string | null;
  hasSourcePdf: boolean;
  lastImportedAt?: string | null;
  fields: AiDocumentField[];
  assets: AiDocumentAsset[];
}

const authHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error(SESSION_EXPIRED_MESSAGE);
  return { Authorization: `Bearer ${token}` };
};

export async function listAssignedDocumentTemplates(): Promise<AiDocumentTemplateOption[]> {
  const res = await axios.get(`${API_URL}/users/ai-document-templates`, {
    headers: authHeaders(),
  });
  return res.data.data ?? [];
}

export async function getQuoteDocument(sectorId: number): Promise<SectorAiDocumentConfig> {
  const res = await axios.get(`${API_URL}/sectors/${sectorId}/ai-documents/quote`, {
    headers: authHeaders(),
  });
  return res.data.data;
}

export async function selectQuoteTemplate(sectorId: number, templateId: number): Promise<SectorAiDocumentConfig> {
  const res = await axios.put(`${API_URL}/sectors/${sectorId}/ai-documents/quote/select-template`, {
    templateId,
  }, {
    headers: authHeaders(),
  });
  return res.data.data;
}

export async function saveQuoteDocument(
  sectorId: number,
  payload: {
    enabled?: boolean;
    documentPrompt?: string;
    linkedAgentId?: number | null;
    linkedAgents?: {
      receptive?: number | null;
      campaign?: number | null;
      followup?: number | null;
      crm?: number | null;
    };
  }
): Promise<SectorAiDocumentConfig> {
  const res = await axios.put(`${API_URL}/sectors/${sectorId}/ai-documents/quote`, payload, {
    headers: authHeaders(),
  });
  return res.data.data;
}

export async function previewQuoteDocument(
  sectorId: number,
  fields: Record<string, string>
): Promise<Blob> {
  const res = await axios.post(`${API_URL}/sectors/${sectorId}/ai-documents/quote/preview`, { fields }, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  return res.data;
}

export function assetUrl(sectorId: number, assetId: string): string {
  return `${API_URL}/sectors/${sectorId}/ai-documents/quote/assets/${assetId}`;
}

/**
 * Motivo legível (em português) de uma falha das chamadas de documentos.
 * A prévia usa `responseType: 'blob'`, então o corpo de erro chega como Blob: lê o JSON antes de traduzir.
 */
export async function documentErrorMessage(error: unknown, fallback: string): Promise<string> {
  const err = error as { response?: { data?: unknown } } | null;
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob && err?.response) {
    try {
      const text = typeof data.text === 'function' ? await data.text() : '';
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        /* corpo em texto puro */
      }
      return apiErrorMessage({ ...(error as object), response: { ...err.response, data: parsed } }, fallback);
    } catch {
      return apiErrorMessage({ ...(error as object), response: { ...err.response, data: undefined } }, fallback);
    }
  }
  return apiErrorMessage(error, fallback);
}
